import { MultiStepFormConfig, BaseFormData } from './components/types';
import { ContactDetailsStep, MultiInputStep, AbnLookupStep, BusinessDetailsStep, TrustDetailsStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  // singleSelectionStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  trustDetailsStepInitialData,
  multiInputStepInitialData,
  combineStepData 
} from './steps/initialData';
import { getUtmParamsForDynamics } from '@/lib/utm-tracking';

// Form options and validations - centralized in config for easy management
export const formOptions = createFormOptions({});

export const formValidations = createFormValidations({
  // countrySelectionValidation: (data: BaseFormData) => {
  //   return !!data.countrySelection;
  // },
  abnLookupStep: (data: BaseFormData): boolean => {
    // For McDonalds Program, ABN/NZBN is required
    const abnValue = data.selectedABN as string;
    if (!abnValue) return false;
    
    // Clean the value and check if it's 11 or 13 digits
    const cleanedValue = abnValue.replace(/\D/g, '');
    return /^\d{11}$|^\d{13}$/.test(cleanedValue);
  },
  businessDetailsStep: (data: BaseFormData) => {
    // Basic validation for business details
    const stateValid = data.countryOfRegistration === "Other" ? true : !!(data.state && data.state !== 'Select');
    return !!(data.countryOfRegistration && data.businessType && stateValid);
  },
  trustDetailsStep: (data: BaseFormData) => {
    // Validation for trust details - only required if business type is Trust
    if (data.businessType !== 'Trust') return true;
    
    // Basic required fields
    const hasBasicFields = !!(data.trusteeType && data.trusteeName);
    
    // ABN/ACN only required if trustee is Corporate Entity
    const needsAbn = data.trusteeType === 'Corporate Entity';
    const hasAbn = needsAbn ? !!data.trustAbnAcn : true;
    
    return hasBasicFields && hasAbn;
  },
  frequentFlyerValidation: () => {
    return true;
  },
});

// Combine initial data from selected steps
const initialFormData = combineStepData(
  // singleSelectionStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  trustDetailsStepInitialData,
  multiInputStepInitialData,
  contactDetailsStepInitialData
  // Add more step data here as you add more steps
);

export const CountryWideFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'country-wide-enquire',
  formOptions: formOptions,
  brandColor: '#3AAD4A',
  brandColorHover: '#096B3C',
  brandColorText: '#FFFFFF',
  brandColorLink: '#096B3C',
  brandColorLinkHover: '#096B3C',
  firstStepBackButtonLink: '/countrywide-program',
  steps: [
    // {
    //   id: 'country-selection',
    //   title: 'Join the McDonald’s Payments Program.',
    //   description: 'Designed for McDonald’s licensees and franchisees in Australia and New Zealand. Register your interest, and we’ll be in touch within 1 business day to complete the onboarding process.',
    //   component: SingleSelectionStep,
    //   validation: formValidations.countrySelectionValidation,
    //   stepConfig: {
    //     fieldName: 'countrySelection',
    //     optionsKey: 'countryOptions',
    //     title: 'Where are you based?',
    //     description: undefined,
    //   }
    // },
    {
      id: 'business-search',
      title: 'Tell us about your business.',
      description: 'Start typing your ABN, ACN, or business name. We\’ll fill in the rest.',
      manualEntryABNRequired: true,
      component: AbnLookupStep,
      validation: formValidations.abnLookupStep,
    },
    {
      id: 'business-metrics',
      title: 'Confirm your business details.',
      description: 'These details help us understand your structure and business needs.',
      component: BusinessDetailsStep,
      stepConfig: {
        showTradingName: true,
        showCountryOfRegistration: true,
        showState: true,
        showBusinessType: true,
        countries: ['Australia'],
      },
      validation: formValidations.businessDetailsStep,
    },
    {
      id: 'trust-details',
      title: 'Tell us more about your trust.',
      description: 'We ask this because finance contracts are held by the trustee on behalf of the trust.',
      component: TrustDetailsStep,
      stepConfig: {
        showTrustType: false,
        showTrusteeType: true,
        showAbnAcn: true,
        showTrusteeName: true,
      },
      validation: formValidations.trustDetailsStep,
      shouldShow: (data: BaseFormData) => data.businessType === 'Trust',
    },
    {
      id: 'frequent-flyer',
      title: 'Want to earn Frequent Flyer points on your payments?',
      description: 'If you\'d like to earn points on eligible payments, enter your membership numbers below. Typically, these would be the same as your ABN number. You can leave these blank. We\'ll follow up during onboarding if needed.',
      component: MultiInputStep,
      stepConfig: {
        inputFields: [
          {
            fieldName: 'qantasBusinessRewards',
            label: 'Qantas Business Rewards number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true, 
            helperText: 'Not a member yet? Join <a href="https://www.qantas.com/au/en/business-rewards/partner/join.html?code=PFOCTET">Qantas Business Rewards</a>',
          },
          {
            fieldName: 'virginAustraliaBusinessFlyer',
            label: 'Virgin Australia Business Flyer number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true,
            helperText: 'Not a member yet? Join <a href="https://www.virginaustralia.com/au/en/fly-for-business/business-flyer/business-flyer-application/?promocode=OCTETMBRM">Virgin Australia Business Flyer</a>',
          }
        ]
      }
    },
    {
      id: 'contact-details',
      title: 'Who should we contact?',
      description: '<strong>How can we reach you?</strong> We’ll get in touch within 1 business day to guide you through the next steps.',
      component: ContactDetailsStep,
      validation: formValidations.contactDetailsStep,
    },
  ],
  initialData: initialFormData,
  thankYouPage: {
    title: 'Thanks for your enquiry',
    subtitle: '<strong>We\’ve received your details.</strong> We will be in touch shortly to discuss your business needs and next steps.',
    firstName: '{{firstName}}',
    nextStepsTitle: 'Here\’s what\’s next for',
    businessName: '{{businessName}}',
    backButtonLink: '/countrywide-program',
    nextStepsItems: [
      {
        icon: 'schedule',
        text: 'Our team will contact you within <strong>1 working day</strong>',
      },
      {
        icon: 'mail',
        text: 'You\’ll receive an <strong>email or a phone call</strong> from our specialist team.'
      },
      {
        icon: 'how_to_reg',
        text: 'We\’ll walk you through the process and <strong>answer any questions</strong>.'
      }
    ],
    ctaButton: {
      text: 'Back to Overview',
      href: '/countrywide-program'
    },
    additionalInfo: 'In the meantime, stay in-the-know with helpful insights and business guides.',
    socialButton: {
      text: 'Follow Octet on LinkedIn',
      href: 'https://www.linkedin.com/company/octet-finance'
    }
  },
  onSubmit: async (data: BaseFormData) => {
    try {
      // Get UTM parameters from sessionStorage
      const utmParams = typeof window !== 'undefined' ? getUtmParamsForDynamics() : {
        ot_utmsource: '',
        ot_utmmedium: '',
        ot_utmterm: '',
        ot_utmcampaign: '',
      };
      const countryWideUtmParams = {
        ...utmParams,
        // ot_utmsource: 'PPP Countrywide AU Landing Page',
        // ot_utmcampaign: 'Co_brand_countrywide',
      };

      // Map form data to Dynamics field format
      const dynamicsPayload = {
        ot_topic: 'PPP Web Lead - Countrywide',
        pb_partner: 'Countrywide',
        pb_title: (data.title as string) || '',
        ot_firstname: (data.firstName as string) || '',
        ot_lastname: (data.lastName as string) || '',
        ot_phone: (data.phone as string) || '',
        ot_workemail: (data.workEmail as string) || '',
        // Business details
        ot_country: (data.countryOfRegistration as string) || '',
        pb_postcode: (data.postcode as string) || '',
        ot_state: (data.state as string) || '',
        ot_businesstype: (data.businessType as string) || '',
        ot_industry: (data.industry === 'Other' ? (data.customIndustry as string) || '' : (data.industry as string) || ''),
        ot_businessname: (data.businessName as string) || '',
        ot_abn: (data.selectedABN as string) || '',
        pb_tradingname: (data.tradingName as string) || '',
        ot_yourenquiry: (data.message as string) || '',
        // Trust details (if applicable)
        pb_trusttype: (data.trusteeType as string) || '',
        pb_trusteename: (data.trusteeName as string) || '',
        pb_trustabnacn: (data.trustAbnAcn as string) || '',
        // Frequent Flyer details
        pb_qantasbusinessrewards: (data.qantasBusinessRewards as string) || '',
        pb_virginaustraliabusinessflyer: (data.virginAustraliaBusinessFlyer as string) || '',
        // Marketing consent
        ot_howdidyouhearaboutus: (data.howDidYouHear as string) || '',
        pb_referredby: (data.referredBy as string) || '',
        ot_subscribedtonewsletter: (data.marketingConsent ? 'true' : 'false'),
        ot_acceptedtermsconditions: (data.termsAccepted ? 'true' : 'false'),
        // UTM parameters
        ...countryWideUtmParams,
        // Include reCAPTCHA token (already added by MultiStepForm)
        recaptchaToken: (data.recaptchaToken as string) || '',
      };
      
      // Submit to Dynamics via API route
      const response = await fetch('/api/dynamics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dynamicsPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      console.info('Form submitted successfully:', result);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let MultiStepForm handle the error display
    }
  },
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true,
  onStepChange: (currentStep, totalSteps) => {
    console.info(`Country Wide Form - Step ${currentStep} of ${totalSteps}`);
  },
};
